import { useState, useEffect } from 'react'
import { fetchDiseases } from 'src/api/filters/phenotypes'

/**
 * Custom hook for managing disease selection
 */
export const useDiseaseSelection = (initialDiseases = [], onDiseasesChange = null) => {
  const [diseases, setDiseases] = useState(initialDiseases)
  const [inheritanceModes, setInheritanceModes] = useState([])

  /**
   * Wrapper for setDiseases that also calls onDiseasesChange if provided
   */
  const updateDiseases = (newDiseases) => {
    setDiseases(newDiseases)
    if (onDiseasesChange && typeof onDiseasesChange === 'function') {
      onDiseasesChange(newDiseases)
    }
  }

  /**
   * Convert diseases array to comma-separated string
   */
  const getDiseasesAsString = () => {
    if (!diseases || diseases.length === 0) return ''
    return diseases.map((disease) => disease.disease).join(', ')
  }

  /**
   * Get diseases as an array of disease names
   * @returns {string[]} Array of disease names
   */
  const getDiseaseNames = () => {
    if (!diseases || diseases.length === 0) return []
    return diseases.map((disease) => disease.disease)
  }

  /**
   * Get diseases as an array of OMIM IDs
   * @returns {string[]} Array of OMIM IDs
   */
  const getDiseaseIds = () => {
    if (!diseases || diseases.length === 0) return []
    return diseases.map((disease) => disease.omim || '')
  }

  /**
   * Parse comma-separated string into diseases array
   */
  const setDiseasesFromString = async (diseasesString) => {
    if (!diseasesString) {
      updateDiseases([])
      return
    }

    const diseaseNames = diseasesString
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean)

    try {
      const results = []

      for (const name of diseaseNames) {
        const response = await fetchDiseases({ search: name })

        if (response.length > 0) {
          const match = response.find((disease) => disease.disease.toLowerCase() === name.toLowerCase()) || response[0]

          results.push(match)
        }
      }

      updateDiseases(results)
    } catch (error) {
      console.error('Error fetching diseases:', error)
    }
  }

  return {
    diseases,
    setDiseases: updateDiseases,
    inheritanceModes,
    setInheritanceModes,
    getDiseasesAsString,
    setDiseasesFromString,
    getDiseaseNames,
    getDiseaseIds,
  }
}

export default useDiseaseSelection
